/**
 * @fileoverview Runs the selection tool inside the preview page: hover highlight, click and drag
 * selection, numbered selections with comments, and the messages exchanged with the chat page.
 */

import {
  addSelectionPaths,
  boundSelection,
  collapseCompleteSelectionContainers,
  intersectsSelectionRect,
  isSelectionRectVisible,
  MAX_SELECTION_NOTE_LENGTH,
  removeSelectionPath,
  type PreviewSelectionItem,
} from './previewSelection';
import './previewSelection.css';

/** One drag or click: the elements it covers and the user's comment on them. */
type SelectionGroup = { paths: string[]; note: string };
type EditorClose = 'commit' | 'cancel' | 'auto';

const SELECTOR = '[data-fabrik-selection-path]';
const UI_SELECTOR = '.fabrik-selection-ui';
/** A pointer that moved less than this between press and release is a click, not a drag. */
const CLICK_MAX_DRAG = 5;
/** The marquee only appears once the pointer has moved this far. */
const MARQUEE_MIN_DRAG = 3;
/** Slack when deciding whether the marquee fully encloses an element. */
const ENCLOSE_TOLERANCE = 1;
/** Text kept from each selected element when it is sent. */
const ELEMENT_TEXT_LIMIT = 2000;
const EDITOR = { width: 410, height: 44, gap: 10, edge: 8 };
const PIN = { height: 22, above: 28, inside: 4, minTop: 30, edge: 4, rightReserve: 30 };
const SVG_NS = 'http://www.w3.org/2000/svg';

export type PreviewSelectionController = {
  /** Handles a message from the chat page; returns whether it was a selection message. */
  handleMessage(message: Record<string, unknown>): boolean;
  /** Removes everything the controller added to the page; sends nothing. */
  dispose(): void;
};

const isUiEvent = (event: Event) =>
  event.target instanceof Element && Boolean(event.target.closest(UI_SELECTOR));

const candidates = () => [...document.querySelectorAll<HTMLElement>(SELECTOR)];

const elementsByPath = () =>
  new Map(candidates().map((element) => [element.dataset.fabrikSelectionPath, element]));

const isTargetVisible = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  const style = getComputedStyle(element);
  if (style.visibility === 'hidden' || style.display === 'none') return false;
  const clips = [];
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const parentStyle = getComputedStyle(parent);
    if (
      /hidden|clip|auto|scroll/.test(
        `${parentStyle.overflow} ${parentStyle.overflowX} ${parentStyle.overflowY}`
      )
    ) {
      const clip = parent.getBoundingClientRect();
      clips.push({ left: clip.left, right: clip.right, top: clip.top, bottom: clip.bottom });
    }
  }
  return isSelectionRectVisible(
    rect,
    { left: 0, right: innerWidth, top: 0, bottom: innerHeight },
    clips
  );
};

/** Topmost selectable element under the pointer, like Chrome's inspect hover. Alt picks the parent. */
const targetAt = (x: number, y: number, alt: boolean) => {
  const stack = document.elementsFromPoint(x, y);
  if (stack[0]?.closest(UI_SELECTOR)) return undefined;
  const element = stack
    .find((candidate) => candidate.closest(SELECTOR))
    ?.closest<HTMLElement>(SELECTOR);
  if (!element) return undefined;
  return alt ? element.parentElement?.closest<HTMLElement>(SELECTOR) ?? element : element;
};

const place = (
  box: HTMLElement,
  rect: { left: number; top: number; width?: number; height?: number }
) => {
  box.style.left = `${rect.left}px`;
  box.style.top = `${rect.top}px`;
  if (rect.width !== undefined) box.style.width = `${rect.width}px`;
  if (rect.height !== undefined) box.style.height = `${rect.height}px`;
};

const element = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string
) => {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

const sameElements = (first: string[], second: string[]) =>
  first.length === second.length && first.every((path) => second.includes(path));

/** The paths a marquee would select as one group, with each element's visibility read only once. */
const marqueePaths = (box: { left: number; right: number; top: number; bottom: number }) => {
  const all = candidates();
  const visibility = new Map<HTMLElement, boolean>();
  const isVisible = (target: HTMLElement) => {
    const known = visibility.get(target);
    if (known !== undefined) return known;
    const visible = isTargetVisible(target);
    visibility.set(target, visible);
    return visible;
  };
  const hit = all.filter(
    (target) => isVisible(target) && intersectsSelectionRect(target.getBoundingClientRect(), box)
  );
  // Anything the marquee fully encloses is selected as one unit (a whole card, grid or form)
  // instead of as its inner items; only partially covered containers fall back to their leaves.
  const enclosed = hit.filter((target) => {
    const rect = target.getBoundingClientRect();
    return (
      rect.left >= box.left - ENCLOSE_TOLERANCE &&
      rect.right <= box.right + ENCLOSE_TOLERANCE &&
      rect.top >= box.top - ENCLOSE_TOLERANCE &&
      rect.bottom <= box.bottom + ENCLOSE_TOLERANCE
    );
  });
  const wholes = enclosed.filter(
    (target) => !enclosed.some((other) => other !== target && other.contains(target))
  );
  const remaining = hit.filter(
    (target) => !wholes.some((whole) => whole !== target && whole.contains(target))
  );
  const deepest = remaining.filter(
    (target) => !remaining.some((other) => target !== other && target.contains(other))
  );
  return collapseCompleteSelectionContainers(
    addSelectionPaths(
      [],
      deepest.map((target) => target.dataset.fabrikSelectionPath!).filter(Boolean)
    ),
    all,
    true,
    isVisible
  );
};

export function createPreviewSelectionController(appId: string): PreviewSelectionController {
  let selectionEnabled = false;
  let groups: SelectionGroup[] = [];
  /** A new selection waits here until the user confirms it with Enter or the send button. */
  let pending: SelectionGroup | undefined;
  let editing: SelectionGroup | undefined;
  let editor: HTMLDivElement | undefined;
  let restoreFocus: HTMLElement | undefined;
  let dragStart: { x: number; y: number } | undefined;
  let dragEnd: { x: number; y: number } | undefined;
  let dragPointerId: number | undefined;
  let marquee: HTMLDivElement | undefined;
  let hoverBox: HTMLDivElement | undefined;
  let hoverTarget: HTMLElement | undefined;
  let markLayer: HTMLDivElement | undefined;
  let previewPaths: string[] = [];
  let lastPointer: { x: number; y: number; alt: boolean } | undefined;
  let frame = 0;
  const frameTasks = new Set<() => void>();

  const post = (message: Record<string, unknown>) =>
    window.parent.postMessage({ ...message, appId }, window.location.origin);
  const requestSend = () => post({ type: 'fabrik:preview-selection-send' });

  /** Runs each queued task at most once per animation frame. */
  const schedule = (task: () => void) => {
    frameTasks.add(task);
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      const tasks = [...frameTasks];
      frameTasks.clear();
      tasks.forEach((run) => run());
    });
  };

  const groupRect = (group: SelectionGroup, byPath = elementsByPath()) => {
    const rects = group.paths.flatMap((path) => {
      const target = byPath.get(path);
      return target && isTargetVisible(target) ? [target.getBoundingClientRect()] : [];
    });
    if (!rects.length) return undefined;
    const left = Math.min(...rects.map((rect) => rect.left));
    const top = Math.min(...rects.map((rect) => rect.top));
    const right = Math.max(...rects.map((rect) => rect.right));
    const bottom = Math.max(...rects.map((rect) => rect.bottom));
    return { left, top, right, bottom, width: right - left, height: bottom - top };
  };

  const positionEditor = () => {
    if (!editor || !editing) return;
    const rect = groupRect(editing);
    if (!rect) {
      closeEditor('cancel');
      return;
    }
    const below = rect.bottom + EDITOR.gap;
    place(editor, {
      left: Math.max(EDITOR.edge, Math.min(rect.left, innerWidth - EDITOR.width - EDITOR.edge)),
      top:
        below + EDITOR.height > innerHeight
          ? Math.max(EDITOR.edge, rect.top - EDITOR.height - EDITOR.gap)
          : below,
    });
  };

  /**
   * Closes the comment bar. Confirming keeps the selection with its comment and cancelling drops
   * a pending one; "auto" (focus moved away) keeps it only when a comment was already typed.
   */
  function closeEditor(how: EditorClose) {
    if (!editor || !editing) return;
    const group = editing;
    const typed = (editor.querySelector('input')?.value ?? '').trim();
    if (how === 'commit' || (how === 'auto' && typed)) {
      group.note = typed;
      if (group === pending) groups.push(group);
    }
    if (group === pending) pending = undefined;
    const box = editor;
    editor = undefined;
    editing = undefined;
    box.remove();
    if (restoreFocus?.isConnected) restoreFocus.focus({ preventScroll: true });
    restoreFocus = undefined;
    sendSelection();
  }

  /** Drops the comment bar and any pending selection without keeping or sending anything. */
  const discardEditor = () => {
    editor?.remove();
    editor = undefined;
    editing = undefined;
    pending = undefined;
  };

  const doneIcon = () => {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('width', '14');
    svg.setAttribute('height', '14');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', 'M8 13V3M3.5 7.5 8 3l4.5 4.5');
    svg.append(path);
    return svg;
  };

  const removeIcon = () => {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('width', '10');
    svg.setAttribute('height', '10');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2.4');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.classList.add('x');
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', 'M3 3l10 10M13 3L3 13');
    svg.append(path);
    return svg;
  };

  function openEditor(group: SelectionGroup, isNew = false) {
    if (editing === group) return;
    closeEditor('auto');
    editing = group;
    if (isNew) pending = group;
    const active = document.activeElement;
    restoreFocus =
      active instanceof HTMLElement && active !== document.body && !active.closest(UI_SELECTOR)
        ? active
        : undefined;
    const number = group === pending ? groups.length + 1 : groups.indexOf(group) + 1;
    editor = element('div', 'fabrik-selection-editor fabrik-selection-ui');
    editor.setAttribute('role', 'group');
    editor.setAttribute('aria-label', `Comment on selection ${number}`);
    const field = document.createElement('input');
    field.type = 'text';
    field.maxLength = MAX_SELECTION_NOTE_LENGTH;
    field.placeholder = 'Add a comment…';
    field.value = group.note;
    field.setAttribute('aria-label', `Comment on selection ${number}`);
    field.addEventListener('keydown', (event) => {
      if (event.isComposing) return;
      if (event.key !== 'Escape' && event.key !== 'Enter') return;
      event.preventDefault();
      closeEditor(event.key === 'Enter' ? 'commit' : 'cancel');
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) requestSend();
    });
    const done = element('button', 'fabrik-selection-editor-done');
    done.type = 'button';
    done.title = 'Add comment (Enter) · Add and send (Ctrl+Enter)';
    done.setAttribute('aria-label', 'Add comment');
    done.append(doneIcon());
    done.addEventListener('click', () => closeEditor('commit'));
    editor.append(
      element('span', 'fabrik-selection-editor-num', String(number)),
      field,
      element('span', 'fabrik-selection-editor-hint', 'Ctrl+↵ send'),
      done
    );
    document.body.append(editor);
    paintMarks();
    field.focus();
    field.setSelectionRange(field.value.length, field.value.length);
  }

  /** Starts a selection on these elements, reopening an identical one instead of duplicating it. */
  const beginGroup = (paths: string[]) => {
    if (!paths.length) return;
    const existing = groups.find((group) => sameElements(group.paths, paths));
    openEditor(existing ?? { paths, note: '' }, !existing);
  };

  const removeGroup = (group: SelectionGroup) => {
    if (editing === group) discardEditor();
    groups = groups.filter((candidate) => candidate !== group);
    sendSelection();
  };

  const buildPin = (group: SelectionGroup, index: number, rect: { left: number; top: number }) => {
    const pin = element(
      'div',
      `fabrik-selection-tag fabrik-selection-ui${group.note ? ' has-note' : ''}`
    );
    const remove = element('button', 'num');
    remove.type = 'button';
    remove.title = 'Remove selection';
    remove.setAttribute('aria-label', `Remove selection ${index + 1}`);
    remove.append(element('span', 'n', String(index + 1)), removeIcon());
    remove.addEventListener('click', () => removeGroup(group));
    const edit = element('button', 'edit', group.note || 'Add comment');
    edit.type = 'button';
    edit.setAttribute('aria-label', `Edit comment on selection ${index + 1}`);
    edit.addEventListener('click', () => openEditor(group));
    pin.append(remove, edit);
    // Keep the pin on screen when its selection is scrolled partly out of view.
    place(pin, {
      left: Math.min(Math.max(PIN.edge, rect.left - 2), innerWidth - PIN.rightReserve),
      top: Math.min(
        Math.max(PIN.edge, rect.top < PIN.minTop ? rect.top + PIN.inside : rect.top - PIN.above),
        innerHeight - PIN.height - PIN.edge
      ),
    });
    return pin;
  };

  /** Draws each queued selection as one framed, numbered pin, plus the live drag preview. */
  function paintMarks() {
    if (!selectionEnabled || (!groups.length && !pending && !previewPaths.length)) {
      markLayer?.remove();
      markLayer = undefined;
      return;
    }
    if (!markLayer) {
      markLayer = element('div', 'fabrik-selection-layer');
      document.body.append(markLayer);
    }
    const byPath = elementsByPath();
    const boxes: HTMLElement[] = [];
    const memberBox = (path: string) => {
      const target = byPath.get(path);
      if (!target || !isTargetVisible(target)) return;
      const box = element('div', 'fabrik-selection-box is-preview');
      place(box, target.getBoundingClientRect());
      boxes.push(box);
    };
    previewPaths.forEach(memberBox);
    [...groups, ...(pending ? [pending] : [])].forEach((group, index) => {
      const rect = groupRect(group, byPath);
      if (!rect) return;
      if (group.paths.length > 1) group.paths.forEach(memberBox);
      const frameBox = element(
        'div',
        `fabrik-selection-box${group === editing ? ' is-editing' : ''}`
      );
      place(frameBox, rect);
      boxes.push(frameBox);
      // While the comment bar is open it carries the number itself, so the pin would repeat it.
      if (group !== editing) boxes.push(buildPin(group, index, rect));
    });
    markLayer.replaceChildren(...boxes);
    positionEditor();
  }

  const paintHover = () => {
    const rect = hoverTarget?.isConnected ? hoverTarget.getBoundingClientRect() : undefined;
    if (!selectionEnabled || !rect || dragStart) {
      hoverBox?.remove();
      hoverBox = undefined;
      return;
    }
    hoverBox ??= document.body.appendChild(element('div', 'fabrik-selection-hover'));
    place(hoverBox, rect);
  };
  const updateHover = () => {
    hoverTarget =
      selectionEnabled && lastPointer && !dragStart
        ? targetAt(lastPointer.x, lastPointer.y, lastPointer.alt)
        : undefined;
    paintHover();
  };
  const repaint = () => {
    paintHover();
    paintMarks();
  };

  const selectionItems = () => {
    const byPath = elementsByPath();
    return groups.flatMap((group, index) => {
      const items = group.paths.flatMap<PreviewSelectionItem>((path) => {
        const target = byPath.get(path);
        if (!target || !isTargetVisible(target)) return [];
        const text = (target.innerText || target.textContent || '').trim();
        return [
          {
            path,
            data: text.slice(0, ELEMENT_TEXT_LIMIT),
            group: index + 1,
            ...(target.dataset.fabrikSelectionDefinitionPath
              ? { definitionPath: target.dataset.fabrikSelectionDefinitionPath }
              : {}),
            ...(text.length > ELEMENT_TEXT_LIMIT ? { truncated: true } : {}),
          },
        ];
      });
      if (group.note && items.length) items[0] = { ...items[0], note: group.note };
      return items;
    });
  };

  function sendSelection(requestId?: string) {
    paintMarks();
    try {
      post({
        type: 'fabrik:preview-selection',
        items: boundSelection(selectionItems()),
        requestId,
      });
    } catch (reason) {
      post({
        type: 'fabrik:preview-selection-error',
        requestId,
        error: reason instanceof Error ? reason.message : 'Selected preview data is too large.',
      });
    }
  }

  const setPreviewPaths = (paths: string[]) => {
    previewPaths = paths;
    paintMarks();
  };
  const clearDrag = () => {
    dragStart = undefined;
    dragEnd = undefined;
    dragPointerId = undefined;
    marquee?.remove();
    marquee = undefined;
    setPreviewPaths([]);
  };

  /** Resizes the marquee to the latest pointer position and previews what it would select. */
  const updateMarquee = () => {
    if (!dragStart || !dragEnd) return;
    const left = Math.min(dragStart.x, dragEnd.x);
    const top = Math.min(dragStart.y, dragEnd.y);
    const width = Math.abs(dragEnd.x - dragStart.x);
    const height = Math.abs(dragEnd.y - dragStart.y);
    if (!marquee && Math.hypot(width, height) >= MARQUEE_MIN_DRAG) {
      marquee = document.body.appendChild(element('div', 'fabrik-selection-marquee'));
    }
    if (!marquee) return;
    place(marquee, { left, top, width, height });
    setPreviewPaths(marqueePaths({ left, right: left + width, top, bottom: top + height }));
  };

  const onPointerDown = (event: PointerEvent) => {
    if (!selectionEnabled || event.button !== 0 || isUiEvent(event)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    dragStart = { x: event.clientX, y: event.clientY };
    dragEnd = dragStart;
    dragPointerId = event.pointerId;
    hoverTarget = undefined;
    paintHover();
    if (event.target instanceof Element) event.target.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event: PointerEvent) => {
    if (isUiEvent(event) && !dragStart) {
      hoverTarget = undefined;
      paintHover();
      return;
    }
    lastPointer = { x: event.clientX, y: event.clientY, alt: event.altKey };
    if (!dragStart) {
      schedule(updateHover);
      return;
    }
    if (event.pointerId !== dragPointerId) return;
    event.preventDefault();
    dragEnd = { x: event.clientX, y: event.clientY };
    schedule(updateMarquee);
  };
  const onPointerCancel = (event: PointerEvent) => {
    if (event.pointerId === dragPointerId) clearDrag();
  };
  const onWindowBlur = () => {
    clearDrag();
    lastPointer = undefined;
    updateHover();
  };
  const onPointerUp = (event: PointerEvent) => {
    if (!selectionEnabled || !dragStart || event.pointerId !== dragPointerId) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const box = {
      left: Math.min(dragStart.x, event.clientX),
      right: Math.max(dragStart.x, event.clientX),
      top: Math.min(dragStart.y, event.clientY),
      bottom: Math.max(dragStart.y, event.clientY),
    };
    const isClick = Math.hypot(box.right - box.left, box.bottom - box.top) < CLICK_MAX_DRAG;
    // Resolve clicks against the point itself: the pointer is captured, so event.target is the
    // pointerdown element, not necessarily what is under the cursor now.
    const clicked = isClick
      ? targetAt(event.clientX, event.clientY, event.altKey)?.dataset.fabrikSelectionPath
      : undefined;
    const paths = isClick ? (clicked ? [clicked] : []) : marqueePaths(box);
    clearDrag();
    lastPointer = { x: event.clientX, y: event.clientY, alt: event.altKey };
    beginGroup(paths);
    updateHover();
  };
  const onClick = (event: MouseEvent) => {
    if (!selectionEnabled || isUiEvent(event)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };
  const onKeyDown = (event: KeyboardEvent) => {
    const withModifier = event.ctrlKey || event.metaKey;
    // Ctrl+Enter sends the queued selections straight from the preview.
    if (event.key === 'Enter' && withModifier && selectionEnabled && groups.length && !editor) {
      event.preventDefault();
      event.stopImmediatePropagation();
      requestSend();
      return;
    }
    // Esc cancels whatever selection is in progress: a drag in flight or an unconfirmed comment.
    if (event.key === 'Escape' && selectionEnabled && (dragStart || editor)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (dragStart) clearDrag();
      else closeEditor('cancel');
      updateHover();
      return;
    }
    if (!selectionEnabled || isUiEvent(event) || event.isComposing) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    // Keyboard selection: Enter on the focused element selects it; Space is only kept from
    // activating the app while the tool is on.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.key === 'Enter' && event.target instanceof Element) {
      const path = event.target.closest<HTMLElement>(SELECTOR)?.dataset.fabrikSelectionPath;
      if (path) beginGroup([path]);
    }
  };

  const listeners: Array<[EventTarget, string, EventListener, boolean?]> = [
    [document, 'pointerdown', onPointerDown as EventListener, true],
    [document, 'pointermove', onPointerMove as EventListener, true],
    [document, 'pointerup', onPointerUp as EventListener, true],
    [document, 'pointercancel', onPointerCancel as EventListener, true],
    [document, 'click', onClick as EventListener, true],
    [document, 'keydown', onKeyDown as EventListener, true],
    [window, 'blur', onWindowBlur],
    [window, 'scroll', () => schedule(repaint), true],
    [window, 'resize', () => schedule(repaint)],
  ];
  listeners.forEach(([target, type, listener, capture]) =>
    target.addEventListener(type, listener, capture)
  );

  const setMode = (enabled: boolean) => {
    if (enabled) {
      selectionEnabled = true;
    } else {
      closeEditor('auto');
      clearDrag();
      selectionEnabled = false;
    }
    document.documentElement.classList.toggle('fabrik-selection-mode', enabled);
    updateHover();
    sendSelection();
  };

  const removePath = (path: string) => {
    [...groups, ...(pending ? [pending] : [])].forEach((group) => {
      group.paths = removeSelectionPath(group.paths, path);
    });
    groups = groups.filter((group) => group.paths.length);
    // A selection that lost all its elements takes its comment bar with it.
    if (editing && !editing.paths.length) discardEditor();
    else if (pending && !pending.paths.length) pending = undefined;
  };

  return {
    handleMessage(message) {
      if (message.appId !== appId) return false;
      switch (message.type) {
        case 'fabrik:preview-selection-mode':
          setMode(message.enabled === true);
          return true;
        case 'fabrik:preview-selection-clear':
          discardEditor();
          groups = [];
          sendSelection();
          return true;
        case 'fabrik:preview-selection-remove':
          if (typeof message.path === 'string') removePath(message.path);
          sendSelection();
          return true;
        case 'fabrik:preview-selection-snapshot':
          // A comment that was typed but not confirmed still belongs to the message being sent.
          closeEditor('auto');
          sendSelection(typeof message.requestId === 'string' ? message.requestId : undefined);
          return true;
        case 'fabrik:preview-selection-focus':
          if (typeof message.path === 'string') {
            candidates()
              .find(
                (target) =>
                  target.dataset.fabrikSelectionPath === message.path ||
                  target.dataset.fabrikSelectionDefinitionPath === message.path
              )
              ?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
          }
          return true;
        default:
          return false;
      }
    },
    dispose() {
      selectionEnabled = false;
      dragStart = undefined;
      cancelAnimationFrame(frame);
      frameTasks.clear();
      listeners.forEach(([target, type, listener, capture]) =>
        target.removeEventListener(type, listener, capture)
      );
      [marquee, hoverBox, editor, markLayer].forEach((node) => node?.remove());
      marquee = undefined;
      hoverBox = undefined;
      editor = undefined;
      markLayer = undefined;
      editing = undefined;
      pending = undefined;
      document.documentElement.classList.remove('fabrik-selection-mode');
    },
  };
}
